import { useQueries, useQuery, UseQueryOptions } from '@tanstack/react-query';

import { useUserInfo } from '@/features/_data/useUserInfo';
import { fetcher } from '@/utils/fetcher';
import { Customer } from '@/types/Customer';
import { Configuration } from '@/types/Configuration';
import { RETAIL_API_URL } from 'src/utils/envConfig';

export const fetchConfigurations = async (
  customerId: string | undefined,
): Promise<Configuration[]> => {
  if (!customerId) {
    return [];
  }
  const result: Configuration[] = await fetcher(
    `${RETAIL_API_URL}/configurations/byCustomer?id=${customerId}`,
  );
  return result;
};

export const useConfigurations = (
  customerId: string | undefined,
  options?: Omit<
    UseQueryOptions<unknown, Error, Configuration[]>,
    'queryKey' | 'queryFn'
  >,
) => {
  const { data: userInfo } = useUserInfo();

  const customerIdOrCurrentCustomer = customerId ?? userInfo?.customer;

  return useQuery({
    queryKey: ['configurations', customerIdOrCurrentCustomer],
    queryFn: () => fetchConfigurations(customerIdOrCurrentCustomer),
    enabled: Boolean(customerIdOrCurrentCustomer),
    ...options,
  });
};

export const useMultipleConfigurations = (customerIds: readonly string[]) => {
  const results = useQueries({
    queries: customerIds.map((customerId) => ({
      queryKey: ['configurations', customerId],
      queryFn: () =>
        fetcher<readonly Customer[]>(
          `${RETAIL_API_URL}/configurations/byCustomer?id=${customerId}`,
        ),
    })),
  });

  return results;
};
